import { commentStyle, KEYWORDS, type TokenKind } from './highlightRules';

export interface CodeToken {
  text: string;
  kind: TokenKind;
}

const SLASH = /\/\/[^\n]*|\/\*[\s\S]*?\*\//;
const HASH = /#[^\n]*/;
const DASH = /--[^\n]*/;
const STRING = /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`/;
const NUMBER = /\b\d[\d_]*(?:\.\d+)?(?:e[+-]?\d+)?\b/i;
const WORD = /[A-Za-z_$][\w$]*/;
const PUNCT = /[{}()[\].,;:!?<>=+\-*/%&|^~]+/;

/** Hard cap — a 4k-line paste must never lock the main thread. */
const MAX_CHARS = 20_000;

const COMMENT_RE: Record<ReturnType<typeof commentStyle>, RegExp> = {
  hash: HASH,
  dash: DASH,
  slash: SLASH,
};

function commentRe(language: string): RegExp {
  return COMMENT_RE[commentStyle(language)];
}

function wordKind(word: string, isCall: boolean, keywords: ReadonlySet<string> | undefined): TokenKind {
  if (keywords?.has(word)) return 'keyword';
  return isCall ? 'fn' : 'plain';
}

/**
 * Dependency-free tokenizer. Highlighting a chat code block does not justify
 * shipping a 200 kB grammar engine, so this covers the constructs that carry
 * 95% of the readability: comments, strings, numbers, keywords, call sites.
 */
export function tokenize(code: string, language = ''): CodeToken[] {
  const source = code.length > MAX_CHARS ? code.slice(0, MAX_CHARS) : code;
  const lang = language.toLowerCase();
  const keywords = KEYWORDS[lang];
  const master = new RegExp(
    `(${commentRe(lang).source})|(${STRING.source})|(${NUMBER.source})|(${WORD.source})|(${PUNCT.source})`,
    'g',
  );

  const tokens: CodeToken[] = [];
  let cursor = 0;

  for (let m = master.exec(source); m !== null; m = master.exec(source)) {
    if (m.index > cursor) tokens.push({ text: source.slice(cursor, m.index), kind: 'plain' });
    cursor = m.index + m[0].length;

    if (m[1] !== undefined) tokens.push({ text: m[1], kind: 'comment' });
    else if (m[2] !== undefined) tokens.push({ text: m[2], kind: 'string' });
    else if (m[3] !== undefined) tokens.push({ text: m[3], kind: 'number' });
    else if (m[4] !== undefined) {
      tokens.push({ text: m[4], kind: wordKind(m[4], source[cursor] === '(', keywords) });
    } else if (m[5] !== undefined) tokens.push({ text: m[5], kind: 'punct' });
  }

  if (cursor < source.length) tokens.push({ text: source.slice(cursor), kind: 'plain' });
  if (code.length > MAX_CHARS) tokens.push({ text: '\n… truncated', kind: 'comment' });
  return tokens;
}
